// components/admin/CarteiraCliente
//
// Saldo, extrato e ajuste manual da carteira de um cliente, para o card
// "Carteira" do detalhe do cliente.
//
// Fica num componente próprio porque é a única parte do painel que cria
// dinheiro: concentrar aqui a confirmação, o motivo obrigatório e o aviso de
// permissão evita espalhar isso pela página do cliente.
import { useEffect, useState } from "react";
import AdminCard from "components/admin/AdminCard";
import StatusBadge from "components/admin/StatusBadge";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  CarteiraDoCliente,
  ajustarCarteira,
  definirCarteiraHabilitada,
  getCarteiraDoCliente,
} from "services/admin/adminCarteira.service";

type Props = { clienteId: string | number };

const ROTULO_LANCAMENTO: Record<string, string> = {
  recarga: "Recarga",
  debito_envio: "Envio",
  estorno: "Estorno de recarga",
  // O backend grava `estorno_envio` no cancelamento da cotação. Sem esta linha
  // o extrato mostrava a string crua.
  estorno_envio: "Estorno de envio",
  ajuste: "Ajuste manual",
};

const brl = (v: number) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const quando = (iso: string | null) => (iso ? new Date(iso).toLocaleString("pt-BR") : "—");

const mensagemErro = (err: unknown, padrao: string) =>
  (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
  (err as Error)?.message ||
  padrao;

const CarteiraCliente = ({ clienteId }: Props) => {
  const [dados, setDados] = useState<CarteiraDoCliente | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const [mostrarAjuste, setMostrarAjuste] = useState(false);
  const [valorTexto, setValorTexto] = useState("");
  const [motivo, setMotivo] = useState("");

  const carregar = () => {
    setLoading(true);
    getCarteiraDoCliente(clienteId)
      .then((d) => {
        setDados(d);
        setErro(null);
      })
      .catch((err) => {
        console.error("[CarteiraCliente] erro:", err);
        setErro(mensagemErro(err, "Não foi possível carregar a carteira."));
      })
      .finally(() => setLoading(false));
  };

  useEffect(carregar, [clienteId]);

  const alternarHabilitada = async () => {
    if (!dados) return;
    setSalvando(true);
    setErro(null);
    setAviso(null);
    try {
      const r = await definirCarteiraHabilitada(clienteId, !dados.habilitada_por_coluna);
      if (r.aviso) setAviso(r.aviso);
      carregar();
    } catch (err) {
      console.error("[CarteiraCliente] erro ao alterar liberação:", err);
      setErro(mensagemErro(err, "Não foi possível alterar a liberação."));
    } finally {
      setSalvando(false);
    }
  };

  const lancarAjuste = async () => {
    const valor = Number(valorTexto.replace(/\./g, "").replace(",", "."));
    if (!Number.isFinite(valor) || valor === 0) {
      setErro("Informe um valor diferente de zero. Use sinal negativo para debitar.");
      return;
    }
    if (motivo.trim().length < 3) {
      setErro("Descreva o motivo do ajuste: o cliente lê isso no extrato dele.");
      return;
    }

    // Dinheiro criado à mão, visível para o cliente: vale confirmar.
    const confirmado = window.confirm(
      `${valor > 0 ? "Creditar" : "Debitar"} ${brl(Math.abs(valor))} na carteira de ` +
        `${dados?.cliente.razaoSocial ?? "este cliente"}?\n\nMotivo: ${motivo.trim()}\n\n` +
        "A ação fica registrada nos logs com o seu usuário."
    );
    if (!confirmado) return;

    setSalvando(true);
    setErro(null);
    setAviso(null);
    try {
      const r = await ajustarCarteira(clienteId, valor, motivo.trim());
      setAviso(`Ajuste lançado. Saldo agora: ${brl(r.saldo)}.`);
      setValorTexto("");
      setMotivo("");
      setMostrarAjuste(false);
      carregar();
    } catch (err) {
      console.error("[CarteiraCliente] erro no ajuste:", err);
      const status = (err as { response?: { status?: number } })?.response?.status;
      setErro(
        status === 403
          ? "Ajuste manual é restrito ao papel admin."
          : mensagemErro(err, "Não foi possível lançar o ajuste.")
      );
    } finally {
      setSalvando(false);
    }
  };

  if (loading && !dados) {
    return (
      <AdminCard title="Carteira">
        <p className="text-sm text-muted-foreground">Carregando...</p>
      </AdminCard>
    );
  }

  if (!dados) {
    return (
      <AdminCard title="Carteira">
        <p className="text-sm text-red-600">{erro || "Carteira indisponível."}</p>
      </AdminCard>
    );
  }

  return (
    <AdminCard
      title="Carteira"
      action={
        <div className="flex items-center gap-2">
          <StatusBadge
            label={dados.habilitada ? "liberada" : "não liberada"}
            tone={dados.habilitada ? "green" : "slate"}
          />
          <Button variant="outline" size="sm" disabled={salvando} onClick={alternarHabilitada}>
            {dados.habilitada_por_coluna ? "Desligar" : "Liberar"}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-3 gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-slate-500">Saldo</p>
            <p
              className={`text-[26px] leading-none font-semibold tabular-nums ${
                dados.saldo_centavos < 0 ? "text-red-600" : "text-slate-900"
              }`}
            >
              {brl(dados.saldo)}
            </p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide text-slate-500">Entrou</p>
            <p className="text-[18px] font-semibold tabular-nums text-green-700">
              {brl(dados.totais.creditado)}
            </p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide text-slate-500">Saiu</p>
            <p className="text-[18px] font-semibold tabular-nums text-slate-700">
              {brl(dados.totais.debitado)}
            </p>
          </div>
        </div>

        {dados.saldo_centavos < 0 ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            Saldo negativo: houve estorno ou chargeback de recarga já gasta. O cliente
            está devendo este valor.
          </div>
        ) : null}

        {!dados.habilitada_por_coluna && dados.habilitada ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            A carteira desta conta está liberada pela variável CARTEIRA_BETA_EMAILS no
            Render, não por este botão. Para desligar de fato, remova o e-mail da variável.
          </div>
        ) : null}

        {aviso ? (
          <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            {aviso}
          </div>
        ) : null}
        {erro ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {erro}
          </div>
        ) : null}

        <div>
          {mostrarAjuste ? (
            <div className="rounded-lg border border-slate-200 p-4 flex flex-col gap-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label>Valor (R$)</Label>
                  <Input
                    value={valorTexto}
                    onChange={(e) => setValorTexto(e.target.value)}
                    placeholder="150,00 ou -150,00"
                  />
                </div>
                <div className="flex flex-col gap-1.5 sm:col-span-2">
                  <Label>Motivo (o cliente lê no extrato)</Label>
                  <Input
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                    placeholder="Ex.: crédito de cortesia pelo atraso do envio ABC-123"
                  />
                </div>
              </div>
              <p className="text-[12px] text-slate-500">
                Valor positivo credita, negativo debita. Débito não pode deixar o saldo
                negativo. A ação vai para os logs com o seu usuário.
              </p>
              <div className="flex items-center gap-2">
                <Button
                  disabled={salvando}
                  className="bg-brand-sidebar hover:bg-brand-sidebar/90"
                  onClick={lancarAjuste}
                >
                  {salvando ? "Lançando..." : "Lançar ajuste"}
                </Button>
                <Button
                  variant="outline"
                  disabled={salvando}
                  onClick={() => {
                    setMostrarAjuste(false);
                    setErro(null);
                  }}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setMostrarAjuste(true)}>
              Lançar ajuste manual
            </Button>
          )}
        </div>

        {dados.boletos.length > 0 ? (
          <div>
            <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">
              Boletos deste cliente
            </p>
            {/* Boleto não passa pela carteira — paga a remessa direto —, mas é a
                outra metade do "este cliente já pagou?". */}
            <div className="overflow-x-auto max-h-56 overflow-y-auto">
              <table className="w-full text-sm">
                <tbody>
                  {dados.boletos.map((b) => (
                    <tr key={b.id} className="border-t border-slate-100">
                      <td className="py-2 pr-4">
                        <p className="text-slate-900">
                          {b.envio ? b.envio.pedido_ref : "Lote (várias cotações)"}
                        </p>
                        <p className="text-[12px] text-slate-500">
                          #{b.id} · vence {b.vencimento ?? "—"}
                        </p>
                      </td>
                      <td className="py-2 pr-4">
                        <StatusBadge
                          label={b.status ?? "—"}
                          tone={
                            ["RECEIVED", "CONFIRMED", "RECEIVED_IN_CASH"].includes(
                              String(b.status)
                            )
                              ? "green"
                              : b.status === "PENDING"
                                ? "yellow"
                                : "slate"
                          }
                        />
                      </td>
                      <td className="py-2 text-right tabular-nums whitespace-nowrap text-slate-800">
                        {brl(b.valor)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[12px] text-slate-500 mt-2">
              Para forçar a baixa de um boleto, use Carteira → aba Boletos.
            </p>
          </div>
        ) : null}

        <div>
          <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">Extrato</p>
          {dados.lancamentos.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum lançamento nesta carteira.</p>
          ) : (
            <div className="overflow-x-auto max-h-80 overflow-y-auto">
              <table className="w-full text-sm">
                <tbody>
                  {dados.lancamentos.map((l) => (
                    <tr key={l.id} className="border-t border-slate-100">
                      <td className="py-2 pr-4">
                        <p className="text-slate-900">
                          {ROTULO_LANCAMENTO[l.tipo] ?? l.tipo}
                        </p>
                        {l.descricao ? (
                          <p className="text-[12px] text-slate-500">{l.descricao}</p>
                        ) : null}
                      </td>
                      <td className="py-2 pr-4 text-[12px] text-slate-500 whitespace-nowrap">
                        {quando(l.criado_em)}
                      </td>
                      <td
                        className={`py-2 text-right tabular-nums whitespace-nowrap ${
                          l.valor_centavos > 0 ? "text-green-700" : "text-slate-800"
                        }`}
                      >
                        {l.valor_centavos > 0 ? "+" : ""}
                        {brl(l.valor)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {dados.totais.truncado ? (
                <p className="text-[12px] text-slate-500 mt-2">
                  Mostrando os {dados.lancamentos.length} lançamentos mais recentes.
                </p>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </AdminCard>
  );
};

export default CarteiraCliente;
